// Formularz tworzenia i edycji zadania w Panelu Handlowca

import React, { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppNavigate } from 'hooks';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { useQueryClient } from 'react-query';
import { TextField, Button, Divider, Skeleton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { reduxActions, useDispatch, useSelector } from 'store';
import { Breadcrumbs, PageTitle } from 'components/controls';
import { WiseSelect } from 'components/wise';
import StatusPill from 'components/wise/WiseTable/components/StatusPill';
import * as BootstrapIcons from 'react-bootstrap-icons';
import {
  useGetSalesCrmClientsDictionary,
  useGetSalesCrmTask,
  useGetSalesCrmTradersDictionary,
  usePostSalesCrmTask,
  usePutSalesCrmTask
} from 'api';
import type { ISalesCrmTask } from 'api/endpoints/useGetSalesCrmTask';
import { ISalesCrmClientsDictionaryItem } from 'api/endpoints/useGetSalesCrmClientsDictionary';
import { ISalesCrmTradersDictionaryItem } from 'api/endpoints/useGetSalesCrmTradersDictionary';

import styles from 'theme/pages/TraderPanelTasksForm/TraderPanelTasksForm.module.scss';

// todo Mockowe opcje – do zastąpienia przez dictioneries z API
const TASK_TYPE_OPTIONS = [
  { value: 'offer', label: 'Oferta' },
  { value: 'call', label: 'Telefon' },
  { value: 'document', label: 'Dokument' },
  { value: 'meeting', label: 'Spotkanie' },
  { value: 'offer', label: 'Inne' }
];

const PRIORITY_OPTIONS = [
  { value: 0, label: 'Niski' },
  { value: 1, label: 'Pilny' },
];

const STATUS_OPTIONS = [
  { value: 0, label: 'Nowe' },
  { value: 1, label: 'W realizacji' },
  { value: 2, label: 'Wykonane' }
];

type OptionItem = { value: number | string; label: string };

export interface ITraderPanelTasksFormValues {
  name: string;
  due_date: string;
  priority: number | null;
  status: string | number;
  status_formatted: string;
  task_type: string | null;
  type: string | null;
  created_by_id: number | null;
  created_by_name?: string;
  trader_id: number | null;
  trader_name: string;
  client_id: number | null;
  client_name: string;
  icon_type: string;
}

const getInitialValues = (task?: ISalesCrmTask | null): ITraderPanelTasksFormValues => {
  if (task) {
    return {
      name: task.name,
      due_date: task.due_date,
      priority: task?.priority,
      status: task.status,
      status_formatted: task.status_formatted,
      type: task.type,
      task_type: task.type_formatted,
      created_by_id: task.createdBy ?? null,
      created_by_name: task.createdByUser?.name_formatted,
      trader_id: task.trader_id,
      trader_name: task.trader?.name_formatted ?? '',
      client_id: task.client_id,
      client_name: task.client?.name ?? '',
      icon_type: task.icon?.type ?? ''
    };
  }
  return {
    name: '',
    due_date: '',
    priority: null,
    status_formatted: '',
    status: 0,
    task_type: null,
    type: null,
    created_by_id: null,
    created_by_name: '',
    trader_id: null,
    trader_name: '',
    client_id: null,
    client_name: '',
    icon_type: ''
  };
};

const inputSx: SxProps<Theme> = {
  '& .MuiInputLabel-root': { lineHeight: 1 },
  '& .MuiInputLabel-root.Mui-focused': { lineHeight: 'normal' },
  '& .MuiInputBase-root': { height: 44, display: 'flex', alignItems: 'center' },
  '& .MuiInputBase-input': { paddingTop: 0, paddingBottom: 0, height: 44, borderRadius: '4px' }
};

interface ITraderPanelTasksFormProps {
  /** W trybie modal komponent: chowa Breadcrumbs/PageTitle, używa onSuccess/onCancel zamiast nawigacji. */
  isModal?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Preselekcja klienta (np. gdy formularz otwierany z karty klienta). */
  client_id?: number | string;
}

const TraderPanelTasksForm: React.FC<ITraderPanelTasksFormProps> = ({
  isModal: isModalProp,
  onSuccess,
  onCancel,
  client_id: preselectedClientIdProp
}) => {
  // Tryb modal aktywny jawnie albo gdy ktoś podał callbacks (np. WiseTableNoContextAction).
  const isModal = isModalProp ?? Boolean(onSuccess || onCancel);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const { profile } = useSelector((state) => state.auth);

  // Preselekcja klienta: prop > query param `client_id` (np. z linku
  // "Dodaj zadanie" w kalendarzu klienta - /trader/tasks/new?client_id=1).
  const preselectedClientId = preselectedClientIdProp ?? searchParams.get('client_id') ?? undefined;
  const taskId = isModal ? undefined : id ? Number(id) : undefined;
  const isEdit = taskId != null && !Number.isNaN(taskId);
  const handleBack = () => {
    if (isModal && onCancel) {
      onCancel();
      return;
    }
    navigate('/trader/tasks');
  };
  const currentUserDisplayName = profile
    ? [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim() || undefined
    : '';

  const {
    data: task,
    isLoading: isTaskLoading,
    error: taskError,
    refetch: refetchTask
  } = useGetSalesCrmTask(taskId || 0, { enabled: !!taskId });

  const { data: clientsOptionsData, isLoading: isClientsOptionsLoading } =
    useGetSalesCrmClientsDictionary();

  const { data: traderOptionsData, isLoading: isTradersOptionsLoading } =
    useGetSalesCrmTradersDictionary();

  const queryClient = useQueryClient();

  const { mutateAsync: createTaskAsync, isLoading: isCreatingTask } =
    usePostSalesCrmTask();

  const { mutateAsync: updateTaskAsync, isLoading: isUpdatingTask } = usePutSalesCrmTask(taskId || 0);

  // Po create/update zadania uniewazniamy cache list zadan (kalendarz, lista zadan, widok klienta),
  // zeby po powrocie na te widoki dane od razu byly odswiezone (a nie czekaly na window-focus / mount-stale).
  const invalidateTasksQueries = () => {
    queryClient.invalidateQueries(['sales-crm', 'tasks']);
    queryClient.invalidateQueries(['sales-crm', 'client-tasks']);
    if (taskId) {
      queryClient.invalidateQueries(['sales-crm', 'task', taskId]);
    }
  };

  const isLoading = isTaskLoading || isClientsOptionsLoading || isTradersOptionsLoading || isUpdatingTask;

  useEffect(() => {
    if (isModal) return;
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Zadania'), path: '/trader/tasks' },
        {
          name: isEdit ? t('Edycja zadania') : t('Nowe zadanie'),
          path: isEdit ? `/trader/tasks/${id}` : '/trader/tasks/new'
        }
      ])
    );
  }, [isEdit, id, isModal]);

  const clientOptions = useMemo(() => {
    return clientsOptionsData?.items.map(
      (option: ISalesCrmClientsDictionaryItem): OptionItem => ({
        value: option.value,
        label: option.text
      })
    );
  }, [clientsOptionsData]);

  const traderOptions = useMemo(() => {
    return traderOptionsData?.items.map(
      (option: ISalesCrmTradersDictionaryItem): OptionItem => ({
        value: option.value,
        label: option.text
      })
    );
  }, [traderOptionsData]);

  // ID handlowca aktualnie zalogowanego (z flagi is_current w słowniku).
  const currentTraderId = useMemo(() => {
    return traderOptionsData?.items.find((item) => item.is_current)?.value ?? null;
  }, [traderOptionsData]);

  const currentTraderName = useMemo(() => {
    return traderOptionsData?.items.find((item) => item.is_current)?.text ?? '';
  }, [traderOptionsData]);

  const { values, handleChange, handleBlur, setFieldValue, touched, errors, handleSubmit } =
    useFormik<ITraderPanelTasksFormValues>({
      initialValues: getInitialValues(task ?? null),
      enableReinitialize: true,
      onSubmit: async () => {
        if (isEdit) {
          await updateTaskAsync({
            name: values.name,
            due_date: values.due_date,
            priority: values.priority,
            status: values.status,
            type: values.type,
            trader_id: values.trader_id,
            client_id: values.client_id
          });

            invalidateTasksQueries();
            refetchTask()
        } else {
            await createTaskAsync({
              name: values.name,
              due_date: values.due_date,
              priority: values.priority,
              status: values.status,
              type: values.type,
              trader_id: values.trader_id,
              client_id: values.client_id
            });

            invalidateTasksQueries();

            if (isModal && onSuccess) {
              onSuccess();
            } else {
              navigate('/trader/tasks');
            }
        }
      }
    });

  // Dla nowego zadania automatycznie ustawiamy handlowca na aktualnie zalogowanego użytkownika.
  useEffect(() => {
    if (isEdit) return;
    if (values.trader_id != null) return;
    if (currentTraderId == null) return;

    setFieldValue('trader_id', currentTraderId);
    setFieldValue('trader_name', currentTraderName);
  }, [isEdit, currentTraderId, currentTraderName, values.trader_id, setFieldValue]);

  // Preselekcja klienta (np. otwarcie z karty klienta `/trader/clients/:id`).
  useEffect(() => {
    if (isEdit) return;
    if (values.client_id != null) return;
    if (preselectedClientId == null) return;

    const parsed = Number(preselectedClientId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;

    setFieldValue('client_id', parsed);
  }, [isEdit, preselectedClientId, values.client_id, setFieldValue]);

  const toWiseOptions = (opts: OptionItem[] | undefined) =>
    opts ? opts.map((item) => ({ value: item.value, label: item.label, item })) : [];

  const pageTitle = isEdit && task ? `${t('Edycja zadania')} - ${task.name}` : t('Nowe zadanie');

  if (isEdit && isLoading) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-TraderPanelTasksForm')}>
        <div className={styles.breadcrumbsWrapper}>
          <Breadcrumbs />
        </div>
        <div className={styles.headerRow}>
          <PageTitle title={t('Edycja zadania')} className={styles.titleOverwrite} />
        </div>
        <div className={styles.formCard}>
          <div className={styles.formCardHeaderRow}>
            <div className={styles.formCardIconWrap}>
              <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1 }} />
            </div>
            <div className={styles.statusPillWrap}>
              <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: 2 }} />
            </div>
          </div>
          <div className={styles.formRows}>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Skeleton variant="text" width={120} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
              <div className={styles.formField}>
                <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
              <div className={styles.formField}>
                <Skeleton variant="text" width={90} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
            </div>
            <div className={styles.formRowDivider}>
              <Divider className={styles.formDivider} />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Skeleton variant="text" width={70} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
              <div className={styles.formField}>
                <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
              <div className={styles.formField}>
                <Skeleton variant="text" width={140} height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={44} sx={{ borderRadius: 1 }} />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.actions}>
                <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rounded" width={80} height={36} sx={{ borderRadius: 1 }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && taskError) {
    return (
      <div className={classnames(styles.componentWrapper, 'StylePath-Pages-TraderPanelTasksForm')}>
        <div className={styles.breadcrumbsWrapper}>
          <Breadcrumbs />
        </div>
        <div className={styles.headerRow}>
          <PageTitle title={t('Błąd')} className={styles.titleOverwrite} />
        </div>
        <div className={classnames(styles.formCard, styles.error)} style={{ padding: 24 }}>
          {t('Nie udało się załadować zadania')}
          <Button
            sx={{ mt: 2 }}
            variant="outlined"
            color="secondary"
            onClick={() => navigate('/trader/tasks')}>
            {t('Wróć do listy')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-TraderPanelTasksForm')}>
      {!isModal && (
        <>
          <div className={styles.breadcrumbsWrapper}>
            <Breadcrumbs />
          </div>
          <div className={styles.headerRow}>
            <PageTitle title={pageTitle} className={styles.titleOverwrite} />
          </div>
        </>
      )}

      <form onSubmit={handleSubmit}>
        <div className={styles.formCard}>
          {(values.icon_type || task?.status || task?.status === 0) && (
            <div className={styles.formCardHeaderRow}>
              {values.icon_type && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <p>Typ zadania</p>
                  <div className={styles.formCardIconWrap}>
                    {(() => {
                      const IconComponent = (BootstrapIcons as any)[
                        values.icon_type
                      ] as React.ComponentType<any>;
                      if (IconComponent) {
                        return (
                          <div className={styles.iconWrapper}>
                            <IconComponent className={styles.iconSvg} />
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              )}
              {(task?.status || task?.status === 0) && (
                <div className={styles.statusPillWrap}>
                  <StatusPill status={String(task?.status)} label={task?.status_formatted} />
                </div>
              )}
            </div>
          )}
          <div className={styles.formRows}>
            {/* Strefa 1: Nazwa i status */}
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <TextField
                  fullWidth
                  color="secondary"
                  sx={inputSx}
                  id="name"
                  name="name"
                  label={t('Nazwa zadania')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
              </div>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Status')}
                  placeholder={t('Wybierz status')}
                  value={values.status}
                  onChange={(item) => setFieldValue('status', item?.value ?? null)}
                  options={toWiseOptions(STATUS_OPTIONS)}
                  clearable={false}
                  disabled={!isEdit}
                />
              </div>
            </div>

            {/* Strefa 2: Typ i priorytet */}
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Typ')}
                  placeholder={t('Wybierz typ zadania')}
                  value={values.type}
                  onChange={(item) => setFieldValue('type', item?.value ?? null)}
                  options={toWiseOptions(TASK_TYPE_OPTIONS)}
                  clearable
                />
              </div>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Priorytet')}
                  placeholder={t('Wybierz priorytet')}
                  value={values.priority}
                  onChange={(item) => setFieldValue('priority', item?.value ?? null)}
                  options={toWiseOptions(PRIORITY_OPTIONS)}
                  clearable
                />
              </div>
            </div>

            <div className={styles.formRowDivider}>
              <Divider className={styles.formDivider} />
            </div>

            {/* Strefa 3: Wybór klienta i handlowca */}
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Klient')}
                  placeholder={t('Wybierz klienta')}
                  value={values.client_id}
                  onChange={(item) => setFieldValue('client_id', (item?.value as number) ?? null)}
                  options={toWiseOptions(clientOptions)}
                  clearable
                />
              </div>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Handlowiec')}
                  placeholder={t('Wybierz handlowca')}
                  value={values.trader_id}
                  onChange={(item) => setFieldValue('trader_id', (item?.value as number) ?? null)}
                  options={toWiseOptions(traderOptions)}
                  clearable
                />
              </div>
            </div>

            {/* Strefa 4: Utworzył, termin realizacji */}
            <div className={styles.formRow}>
              <div className={styles.formField}>
                <WiseSelect<OptionItem>
                  styleGroup="trader-panel-form"
                  label={t('Utworzył')}
                  placeholder={t('Aktualny handlowiec')}
                  value={values.created_by_id ?? profile?.id}
                  onChange={() => {
                    // disabled
                  }}
                  options={
                    profile
                      ? toWiseOptions([
                          { value: profile.id, label: currentUserDisplayName ?? '' },
                          { value: values.created_by_id ?? 0, label: values.created_by_name ?? '' }
                        ])
                      : []
                  }
                  disabled
                  clearable={false}
                />
              </div>
              <div className={styles.formField}>
                <TextField
                  fullWidth
                  color="secondary"
                  sx={inputSx}
                  id="due_date"
                  name="due_date"
                  label={t('Termin realizacji')}
                  type="datetime-local"
                  value={values.due_date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(touched.due_date && errors.due_date)}
                  helperText={touched.due_date && errors.due_date}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.actions}>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={!isEdit && (isCreatingTask || isUpdatingTask)}>
                  {isEdit ? t('Zapisz') : t('Utwórz zadanie')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={handleBack}>
                  {isModal ? t('Anuluj') : t('Wróć')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TraderPanelTasksForm;
